import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../ui/tabs";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import {
  ArrowLeft,
  DollarSign,
  Save,
  User,
  Mail,
  Phone,
  Calendar,
  Hash,
  Briefcase,
  Users,
  Building2,
  CheckCircle2,
  AlertCircle,
  TrendingUp,
  TrendingDown,
  Wallet,
  Sparkles,
  RefreshCw,
} from "lucide-react";
import { toast } from "../../hooks/use-toast";
import { salaryFieldService } from "../../services/salaryFieldService";
import { userService } from "../../services/userService";
import { mockSalaryFields } from "../../mockData";
import { useAuth } from "../../context/AuthContext";

export const EmployeeSalaryManager = ({
  employee,
  onBack,
  onEmployeeUpdate,
}) => {
  const { user } = useAuth();

  const [activeTab, setActiveTab] = useState("employee-info");

  // High-level salary fields
  const [totalCompensation, setTotalCompensation] = useState("");
  const [variablePay, setVariablePay] = useState("");

  // Component values mapping: { [salaryFieldId]: amount }
  const [componentValues, setComponentValues] = useState({});

  // Salary components loaded from API
  const [companyComponents, setCompanyComponents] = useState([]);
  const [loadingComponents, setLoadingComponents] = useState(true);
  const [componentsSource, setComponentsSource] = useState("api"); // 'api' | 'local' | 'default'

  // Saving state
  const [savingSalary, setSavingSalary] = useState(false);
  const [saveSuccessMessage, setSaveSuccessMessage] = useState("");

  const effectiveOrgId =
    employee?.orgId ||
    employee?.org_id ||
    user?.org_id ||
    user?.orgId ||
    user?.org?.id ||
    1;

  const employeeId = employee?.id || employee?.employeeId;

  // Format currency helper
  const formatCurrency = (val) => {
    const num = Number(val);
    if (!Number.isFinite(num) || isNaN(num)) return "₹0";
    return `₹${num.toLocaleString("en-IN")}`;
  };

  // Helper for full employee name
  const fullName = useMemo(() => {
    if (!employee) return "Employee";
    const middle =
      employee.middleName &&
      !["no", "none", "n/a", "null"].includes(
        String(employee.middleName).toLowerCase().trim(),
      )
        ? employee.middleName
        : null;
    return (
      [employee.firstName, middle, employee.lastName]
        .filter(Boolean)
        .join(" ") ||
      employee.name ||
      employee.username ||
      employee.email ||
      `Employee #${employeeId}`
    );
  }, [employee, employeeId]);

  // Load Company Salary Components & Existing Employee Salary Values
  const loadSalaryData = useCallback(async () => {
    if (!employeeId) return;
    setLoadingComponents(true);

    let resolvedFields = mockSalaryFields;
    let assignedFieldIds = [];

    try {
      // 1. Fetch Super list of all salary fields
      try {
        const fieldsResp = await salaryFieldService.getAllSalaryFields();
        const list = Array.isArray(fieldsResp)
          ? fieldsResp
          : fieldsResp?.data ||
            fieldsResp?.salaryFields ||
            fieldsResp?.fields ||
            [];
        if (list.length > 0) {
          resolvedFields = list;
        } else {
          resolvedFields = mockSalaryFields;
        }
      } catch (err) {
        console.warn(
          "Failed to fetch super list of salary fields, using mock definitions:",
          err,
        );
        resolvedFields = mockSalaryFields;
      }

      // 2. Fetch assigned company payroll components for effectiveOrgId
      try {
        const compResp =
          await salaryFieldService.getCompanyPayrollComponents(effectiveOrgId);
        if (Array.isArray(compResp)) {
          assignedFieldIds = compResp
            .map((item) =>
              typeof item === "object" ? item.id || item.salaryFieldId : item,
            )
            .filter(Boolean);
        } else if (
          compResp?.salaryFieldIds &&
          Array.isArray(compResp.salaryFieldIds)
        ) {
          assignedFieldIds = compResp.salaryFieldIds;
        } else if (compResp?.data && Array.isArray(compResp.data)) {
          assignedFieldIds = compResp.data
            .map((item) =>
              typeof item === "object" ? item.id || item.salaryFieldId : item,
            )
            .filter(Boolean);
        }
        setComponentsSource("api");
      } catch (apiErr) {
        console.warn(
          "Could not load company components from API, checking localStorage:",
          apiErr,
        );
      }

      // If API did not return assigned IDs, check local storage key saved by Settings page
      if (!assignedFieldIds || assignedFieldIds.length === 0) {
        const localKey = `company_payroll_components_${effectiveOrgId}`;
        const cached = localStorage.getItem(localKey);
        if (cached) {
          try {
            const parsed = JSON.parse(cached);
            if (Array.isArray(parsed) && parsed.length > 0) {
              assignedFieldIds = parsed;
              setComponentsSource("local");
            }
          } catch (e) {
            // ignore JSON parse error
          }
        }
      }

      // Default fallback component IDs if neither API nor local storage has them
      if (!assignedFieldIds || assignedFieldIds.length === 0) {
        assignedFieldIds = [1, 2, 3, 5, 6, 7, 9]; // Basic, HRA, Medical, Bonus, Special, PF, Prof Tax
        setComponentsSource("default");
      }

      // Filter resolvedFields to only include those assigned to this company
      const activeComponents = resolvedFields.filter((field) =>
        assignedFieldIds.includes(field.id || field.salaryFieldId),
      );

      // If for some reason activeComponents is empty, fall back to resolvedFields
      const finalComponents =
        activeComponents.length > 0 ? activeComponents : resolvedFields;
      setCompanyComponents(finalComponents);

      // 3. Load existing employee salary data
      let existingSalary = null;

      // Check localStorage first for instant responsiveness
      const localSalaryKey = `employee_salary_${employeeId}`;
      const cachedSalary = localStorage.getItem(localSalaryKey);
      if (cachedSalary) {
        try {
          existingSalary = JSON.parse(cachedSalary);
        } catch (e) {
          // ignore
        }
      }

      // Then attempt to fetch from API
      try {
        const now = new Date();
        const apiSalary = await userService.getSalary(employeeId, {
          orgId: effectiveOrgId,
          month: now.getMonth() + 1,
          year: now.getFullYear(),
        });
        if (apiSalary && typeof apiSalary === "object") {
          existingSalary = apiSalary;
        }
      } catch (salaryApiErr) {
        // Fallback: check getCompensations
        try {
          const apiComp = await userService.getCompensations(
            employeeId,
            effectiveOrgId,
          );
          if (apiComp && typeof apiComp === "object") {
            existingSalary = apiComp;
          }
        } catch (compErr) {
          // Both API endpoints unavailable; cachedSalary or defaults will be used
        }
      }

      // Populate high-level and component fields
      if (existingSalary) {
        if (
          existingSalary.totalCompensation !== undefined &&
          existingSalary.totalCompensation !== null
        ) {
          setTotalCompensation(String(existingSalary.totalCompensation));
        } else if (existingSalary.grossSalary) {
          setTotalCompensation(String(existingSalary.grossSalary * 12));
        } else if (employee?.grossSalary) {
          setTotalCompensation(String(employee.grossSalary * 12));
        }

        if (
          existingSalary.variable !== undefined &&
          existingSalary.variable !== null
        ) {
          setVariablePay(String(existingSalary.variable));
        } else if (existingSalary.variablePay !== undefined) {
          setVariablePay(String(existingSalary.variablePay));
        } else {
          setVariablePay("0");
        }

        const valuesMap = {};
        if (Array.isArray(existingSalary.components)) {
          existingSalary.components.forEach((comp) => {
            const fieldId = comp.salaryFieldId || comp.id;
            if (fieldId) {
              valuesMap[fieldId] =
                comp.value !== null && comp.value !== undefined
                  ? String(comp.value)
                  : "";
            }
          });
        }
        setComponentValues(valuesMap);
      } else {
        // Defaults from employee object if present
        if (employee?.grossSalary) {
          setTotalCompensation(String(employee.grossSalary * 12));
        }
        setVariablePay("0");
      }
    } catch (error) {
      console.error("Error loading salary components:", error);
      toast({
        title: "Could not load salary components",
        description: "Standard payroll components loaded for configuration.",
        variant: "destructive",
      });
    } finally {
      setLoadingComponents(false);
    }
  }, [effectiveOrgId, employeeId, employee]);

  useEffect(() => {
    loadSalaryData();
  }, [loadSalaryData]);

  // Derived financial calculations
  const parsedTotalComp = Number(totalCompensation) || 0;
  const parsedVariable = Number(variablePay) || 0;
  const parsedFixedPay = Math.max(0, parsedTotalComp - parsedVariable);
  const monthlyGrossFixed =
    parsedFixedPay > 0 ? Math.round(parsedFixedPay / 12) : 0;

  // Earnings & Deductions breakdown from componentValues
  const {
    totalEarningsMonthly,
    totalDeductionsMonthly,
    netTakeHomeMonthly,
    componentsBreakdown,
  } = useMemo(() => {
    let earnMonthly = 0;
    let dedMonthly = 0;

    const breakdown = companyComponents.map((comp) => {
      const fieldId = comp.id || comp.salaryFieldId;
      const rawVal = componentValues[fieldId];
      const monthlyVal = Number(rawVal) || 0;
      const annualVal = monthlyVal * 12;
      const isEarning =
        String(comp.fieldType || "").toUpperCase() === "EARNING";

      if (isEarning) {
        earnMonthly += monthlyVal;
      } else {
        dedMonthly += monthlyVal;
      }

      const percentOfFixed =
        parsedFixedPay > 0
          ? ((annualVal / parsedFixedPay) * 100).toFixed(1)
          : 0;

      return {
        ...comp,
        fieldId,
        monthlyVal,
        annualVal,
        isEarning,
        percentOfFixed,
      };
    });

    return {
      totalEarningsMonthly: earnMonthly,
      totalDeductionsMonthly: dedMonthly,
      netTakeHomeMonthly: Math.max(0, earnMonthly - dedMonthly),
      componentsBreakdown: breakdown,
    };
  }, [companyComponents, componentValues, parsedFixedPay]);

  // Handle component value change
  const handleComponentChange = (fieldId, value) => {
    // Only allow numbers and decimal
    const cleanValue = value.replace(/[^0-9.]/g, "");
    setComponentValues((prev) => ({
      ...prev,
      [fieldId]: cleanValue,
    }));
  };

  // Auto-populate recommended distribution based on Fixed CTC
  const handleAutoDistribute = () => {
    if (parsedFixedPay <= 0) {
      toast({
        title: "Please enter Total Compensation first",
        description:
          "A valid annual compensation is needed to distribute component values.",
        variant: "destructive",
      });
      return;
    }

    const monthlyFixed = Math.round(parsedFixedPay / 12);
    const newValues = { ...componentValues };

    // Standard Indian payroll structure recommendations:
    // Basic: ~50% of Fixed
    // HRA: ~25% of Fixed
    // PF: 12% of Basic or standard capped
    // Special Allowance: Remaining balance of earnings
    let allocatedEarnings = 0;

    companyComponents.forEach((comp) => {
      const name = (comp.name || "").toLowerCase();
      const fieldId = comp.id || comp.salaryFieldId;
      const isEarning =
        String(comp.fieldType || "").toUpperCase() === "EARNING";

      if (isEarning) {
        if (name.includes("basic")) {
          const basic = Math.round(monthlyFixed * 0.5);
          newValues[fieldId] = String(basic);
          allocatedEarnings += basic;
        } else if (name.includes("hra") || name.includes("house rent")) {
          const hra = Math.round(monthlyFixed * 0.25);
          newValues[fieldId] = String(hra);
          allocatedEarnings += hra;
        } else if (name.includes("medical")) {
          const med = Math.min(1250, Math.round(monthlyFixed * 0.03));
          newValues[fieldId] = String(med);
          allocatedEarnings += med;
        } else if (name.includes("special allowance")) {
          // will calculate remainder
        } else if (!newValues[fieldId]) {
          newValues[fieldId] = "0";
        }
      } else {
        // Deductions
        if (name.includes("pf") || name.includes("provident")) {
          const basicMonthly = Math.round(monthlyFixed * 0.5);
          const pf = Math.min(1800, Math.round(basicMonthly * 0.12));
          newValues[fieldId] = String(pf);
        } else if (name.includes("professional tax")) {
          newValues[fieldId] = "200";
        } else if (!newValues[fieldId]) {
          newValues[fieldId] = "0";
        }
      }
    });

    // Allocate remainder to Special Allowance if present
    const specialComp = companyComponents.find((c) =>
      (c.name || "").toLowerCase().includes("special allowance"),
    );
    if (specialComp) {
      const specialId = specialComp.id || specialComp.salaryFieldId;
      const remainder = Math.max(0, monthlyFixed - allocatedEarnings);
      newValues[specialId] = String(remainder);
    }

    setComponentValues(newValues);
    toast({
      title: "Standard Template Applied",
      description:
        "Salary components calculated based on your Fixed Compensation.",
    });
  };

  // Handle Save
  const handleSaveSalary = async () => {
    setSavingSalary(true);
    setSaveSuccessMessage("");

    const payload = {
      userId: employeeId,
      orgId: effectiveOrgId,
      totalCompensation: parsedTotalComp,
      variable: parsedVariable,
      variablePay: parsedVariable,
      fixedCompensation: parsedFixedPay,
      month: new Date().getMonth() + 1,
      year: new Date().getFullYear(),
      components: companyComponents.map((comp) => {
        const fieldId = comp.id || comp.salaryFieldId;
        const val = componentValues[fieldId];
        return {
          salaryFieldId: fieldId,
          name: comp.name,
          fieldType: comp.fieldType,
          value: val !== "" && val !== undefined ? Number(val) : 0,
        };
      }),
    };

    try {
      // 1. Save to API
      try {
        await userService.saveSalary(employeeId, payload);
      } catch (err) {
        console.warn(
          "userService.saveSalary endpoint failed, trying saveCompensations:",
          err,
        );
        try {
          await userService.saveCompensations(employeeId, payload);
        } catch (compErr) {
          console.warn(
            "userService.saveCompensations also failed; saving locally:",
            compErr,
          );
        }
      }

      // 2. Persist to localStorage for guaranteed offline/dev persistence
      const localSalaryKey = `employee_salary_${employeeId}`;
      localStorage.setItem(localSalaryKey, JSON.stringify(payload));

      setSaveSuccessMessage("Salary details saved successfully!");
      toast({
        title: "Salary Saved Successfully",
        description: `Compensation and salary components updated for ${fullName}.`,
      });

      if (onEmployeeUpdate) {
        onEmployeeUpdate({
          ...employee,
          totalCompensation: parsedTotalComp,
          grossSalary: Math.round(parsedFixedPay / 12),
        });
      }
    } catch (error) {
      console.error("Failed to save salary details:", error);
      toast({
        title: "Error saving salary details",
        description: error.message || "Failed to persist salary configuration.",
        variant: "destructive",
      });
    } finally {
      setSavingSalary(false);
    }
  };

  const hasManager = Boolean(employee?.managerUserId || employee?.managerEmail);
  const subordinatesList = Array.isArray(employee?.subordinates)
    ? employee.subordinates
    : [];
  const hasSubordinates = subordinatesList.length > 0;

  return (
    <div className="space-y-6">
      {/* Top Navigation & Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-2 border-b">
        <div className="flex items-center space-x-3">
          {onBack && (
            <Button
              variant="outline"
              size="sm"
              onClick={onBack}
              className="h-9 px-3 text-gray-700 hover:text-gray-900"
            >
              <ArrowLeft className="h-4 w-4 mr-1.5" /> Back to Employees
            </Button>
          )}
          <div>
            <h1 className="text-2xl font-bold text-gray-900 tracking-tight flex items-center gap-2">
              <span>{fullName}</span>
              <Badge
                variant={employee?.enabled !== false ? "default" : "secondary"}
              >
                {employee?.enabled !== false ? "Active" : "Disabled"}
              </Badge>
            </h1>
            <p className="text-sm text-gray-500">
              {employee?.designation || "Employee"} &bull;{" "}
              {employee?.department || "General"} &bull;{" "}
              <span className="font-mono">
                {employee?.employeeId || employeeId}
              </span>
            </p>
          </div>
        </div>

        {activeTab === "salary-details" && (
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleAutoDistribute}
              className="text-blue-600 border-blue-200 hover:bg-blue-50"
            >
              <Sparkles className="h-4 w-4 mr-1.5 text-blue-500" />
              Auto-Distribute Components
            </Button>
            <Button
              size="sm"
              onClick={handleSaveSalary}
              disabled={savingSalary}
              className="bg-blue-600 hover:bg-blue-700 text-white shadow-sm"
            >
              {savingSalary ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" /> Save Salary Details
                </>
              )}
            </Button>
          </div>
        )}
      </div>

      {/* Tabs Layout */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-2 bg-gray-100 p-1 rounded-xl">
          <TabsTrigger
            value="employee-info"
            className="flex items-center gap-2 data-[state=active]:bg-white data-[state=active]:text-blue-700 data-[state=active]:shadow-sm font-medium"
          >
            <User className="h-4 w-4" />
            <span>Employee Info</span>
          </TabsTrigger>
          <TabsTrigger
            value="salary-details"
            className="flex items-center gap-2 data-[state=active]:bg-white data-[state=active]:text-blue-700 data-[state=active]:shadow-sm font-medium"
          >
            <DollarSign className="h-4 w-4" />
            <span>Salary Details</span>
          </TabsTrigger>
        </TabsList>

        {/* TAB 1: EMPLOYEE INFO */}
        <TabsContent value="employee-info" className="space-y-6 pt-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Quick Profile Summary Card */}
            <Card className="lg:col-span-1 shadow-sm border-gray-200">
              <CardContent className="pt-6 flex flex-col items-center text-center">
                <div className="w-24 h-24 rounded-full bg-gradient-to-tr from-blue-600 to-indigo-500 text-white flex items-center justify-center text-3xl font-bold shadow-md mb-4 ring-4 ring-blue-50">
                  {employee?.firstName?.charAt(0) ||
                    employee?.email?.charAt(0) ||
                    "U"}
                </div>
                <h2 className="text-xl font-bold text-gray-900">{fullName}</h2>
                <p className="text-sm font-medium text-gray-600 mt-1">
                  {employee?.designation || "Not Specified"}
                </p>
                <Badge
                  variant="outline"
                  className="mt-2 text-xs font-semibold px-2.5 py-0.5 bg-blue-50 text-blue-700 border-blue-200"
                >
                  {employee?.department || "Department N/A"}
                </Badge>

                <div className="w-full border-t border-gray-100 mt-6 pt-4 text-left space-y-2.5 text-sm">
                  <div className="flex justify-between py-1">
                    <span className="text-gray-500">Employee ID</span>
                    <span className="font-mono font-medium text-gray-800">
                      {employee?.employeeId || employeeId}
                    </span>
                  </div>
                  <div className="flex justify-between py-1">
                    <span className="text-gray-500">Status</span>
                    <span className="font-medium text-green-600">
                      {employee?.enabled !== false ? "Active" : "Disabled"}
                    </span>
                  </div>
                  <div className="flex justify-between py-1">
                    <span className="text-gray-500">Role</span>
                    <span className="font-medium text-gray-800">
                      {employee?.userRoles?.join(", ") ||
                        employee?.roles?.join(", ") ||
                        employee?.role ||
                        "Employee"}
                    </span>
                  </div>
                  <div className="flex justify-between py-1">
                    <span className="text-gray-500">Joined</span>
                    <span className="font-medium text-gray-800">
                      {employee?.joiningDate || "-"}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Detailed Info Card */}
            <Card className="lg:col-span-2 shadow-sm border-gray-200">
              <CardHeader className="pb-3 border-b">
                <CardTitle className="text-lg">
                  Personal & Contact Information
                </CardTitle>
                <CardDescription>
                  Existing employee records and demographics
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="flex items-start space-x-3 p-3 rounded-lg bg-gray-50 border border-gray-100">
                    <Hash className="h-5 w-5 text-blue-500 mt-0.5" />
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Employee ID
                      </p>
                      <p className="font-mono font-medium text-gray-900 mt-0.5">
                        {employee?.employeeId || employeeId}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3 p-3 rounded-lg bg-gray-50 border border-gray-100">
                    <Mail className="h-5 w-5 text-blue-500 mt-0.5" />
                    <div className="overflow-hidden">
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Email Address
                      </p>
                      <p className="font-medium text-gray-900 mt-0.5 truncate">
                        {employee?.email || "-"}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3 p-3 rounded-lg bg-gray-50 border border-gray-100">
                    <Phone className="h-5 w-5 text-blue-500 mt-0.5" />
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Mobile Number
                      </p>
                      <p className="font-medium text-gray-900 mt-0.5">
                        {employee?.mobile || "-"}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3 p-3 rounded-lg bg-gray-50 border border-gray-100">
                    <Calendar className="h-5 w-5 text-blue-500 mt-0.5" />
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Joining Date
                      </p>
                      <p className="font-medium text-gray-900 mt-0.5">
                        {employee?.joiningDate || "-"}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3 p-3 rounded-lg bg-gray-50 border border-gray-100">
                    <Briefcase className="h-5 w-5 text-blue-500 mt-0.5" />
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Department
                      </p>
                      <p className="font-medium text-gray-900 mt-0.5">
                        {employee?.department || "-"}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3 p-3 rounded-lg bg-gray-50 border border-gray-100">
                    <Building2 className="h-5 w-5 text-blue-500 mt-0.5" />
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Designation
                      </p>
                      <p className="font-medium text-gray-900 mt-0.5">
                        {employee?.designation || "-"}
                      </p>
                    </div>
                  </div>

                  {employee?.emergencyContactName && (
                    <div className="flex items-start space-x-3 p-3 rounded-lg bg-gray-50 border border-gray-100">
                      <User className="h-5 w-5 text-amber-500 mt-0.5" />
                      <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                          Emergency Contact
                        </p>
                        <p className="font-medium text-gray-900 mt-0.5">
                          {employee.emergencyContactName}{" "}
                          {employee.emergencyContactMobile
                            ? `(${employee.emergencyContactMobile})`
                            : ""}
                        </p>
                      </div>
                    </div>
                  )}

                  {employee?.linkedin && (
                    <div className="flex items-start space-x-3 p-3 rounded-lg bg-gray-50 border border-gray-100">
                      <Briefcase className="h-5 w-5 text-indigo-500 mt-0.5" />
                      <div className="overflow-hidden">
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                          LinkedIn
                        </p>
                        <a
                          href={employee.linkedin}
                          target="_blank"
                          rel="noreferrer"
                          className="font-medium text-blue-600 hover:underline mt-0.5 truncate block"
                        >
                          {employee.linkedin}
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Organization Structure Hierarchy Card */}
          <Card className="shadow-sm border-gray-200">
            <CardHeader className="pb-3 border-b">
              <CardTitle className="text-lg flex items-center space-x-2">
                <Users className="h-5 w-5 text-blue-600" />
                <span>Organization Hierarchy</span>
              </CardTitle>
              <CardDescription>
                Reporting relationship and direct reports
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="flex flex-col items-center justify-center p-6 bg-slate-50 rounded-xl border border-slate-200">
                {/* Manager Node */}
                {hasManager ? (
                  <div className="px-6 py-3.5 bg-white border border-blue-200 rounded-xl shadow-sm flex items-center space-x-3.5">
                    <div className="w-10 h-10 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-bold">
                      {(employee?.managerEmail?.charAt(0) || "M").toUpperCase()}
                    </div>
                    <div>
                      <span className="inline-block text-[10px] font-bold text-blue-600 bg-blue-50 px-2 py-0.5 rounded uppercase tracking-wider">
                        Reporting Manager
                      </span>
                      <p className="font-semibold text-gray-900 mt-0.5">
                        {employee.managerEmail
                          ? employee.managerEmail.split("@")[0]
                          : `User #${employee.managerUserId}`}
                      </p>
                      <p className="text-xs text-gray-500">
                        {employee.managerEmail ||
                          `ID: ${employee.managerUserId}`}
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="px-4 py-2 bg-gray-100 border border-gray-200 rounded-md text-xs text-gray-500 font-medium">
                    No Direct Manager Assigned
                  </div>
                )}

                {/* Vertical Line */}
                <div className="h-8 border-l-2 border-dashed border-gray-300"></div>

                {/* Current Employee Node */}
                <div className="px-6 py-3.5 bg-blue-600 text-white border border-blue-700 rounded-xl shadow-md flex items-center space-x-3.5 ring-4 ring-blue-100">
                  <div className="w-10 h-10 rounded-full bg-blue-500 text-white flex items-center justify-center font-bold border border-blue-400">
                    {(
                      employee?.firstName?.charAt(0) ||
                      employee?.email?.charAt(0) ||
                      "U"
                    ).toUpperCase()}
                  </div>
                  <div>
                    <span className="inline-block text-[10px] font-bold text-blue-100 bg-blue-700/60 px-2 py-0.5 rounded uppercase tracking-wider">
                      Current Employee
                    </span>
                    <p className="font-semibold text-white mt-0.5">
                      {fullName}
                    </p>
                    <p className="text-xs text-blue-100">
                      {employee?.designation || "Employee"}
                    </p>
                  </div>
                </div>

                {/* Subordinates Node */}
                {hasSubordinates ? (
                  <>
                    <div className="h-8 border-l-2 border-dashed border-gray-300"></div>
                    {subordinatesList.length > 1 && (
                      <div
                        className="border-t-2 border-dashed border-gray-300 mb-0"
                        style={{
                          width: `${Math.min(subordinatesList.length * 170, 580)}px`,
                        }}
                      ></div>
                    )}
                    <div className="flex flex-wrap justify-center gap-4 mt-3">
                      {subordinatesList.map((sub, idx) => {
                        const subId = sub.userId || sub.id;
                        const subName = sub.firstName
                          ? `${sub.firstName} ${sub.lastName || ""}`.trim()
                          : sub.email
                            ? sub.email.split("@")[0]
                            : `User #${subId}`;
                        return (
                          <div
                            key={subId || idx}
                            className="flex flex-col items-center"
                          >
                            <div className="h-3 border-l-2 border-dashed border-gray-300 -mt-3 mb-1"></div>
                            <div className="px-4 py-2.5 bg-white border border-gray-200 rounded-lg shadow-sm flex items-center space-x-2.5">
                              <div className="w-7 h-7 rounded-full bg-gray-100 text-gray-700 flex items-center justify-center text-xs font-bold">
                                {(
                                  sub.firstName?.charAt(0) ||
                                  sub.email?.charAt(0) ||
                                  "U"
                                ).toUpperCase()}
                              </div>
                              <div>
                                <p className="font-medium text-gray-900 text-xs">
                                  {subName}
                                </p>
                                <p className="text-[10px] text-gray-500">
                                  {sub.email || `ID: ${subId}`}
                                </p>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="h-6 border-l-2 border-dashed border-gray-200"></div>
                    <div className="px-4 py-2 bg-gray-100 border border-gray-200 rounded-md text-xs text-gray-500 font-medium">
                      No Direct Reports / Subordinates
                    </div>
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* TAB 2: SALARY DETAILS */}
        <TabsContent value="salary-details" className="space-y-6 pt-4">
          {/* Section 1: High Level Fields (Editable) */}
          <Card className="shadow-sm border-gray-200">
            <CardHeader className="pb-4 border-b">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Wallet className="h-5 w-5 text-blue-600" />
                    <span>1. High-Level Compensation Fields</span>
                  </CardTitle>
                  <CardDescription>
                    Configure annual compensation packages and variable
                    compensation ratio
                  </CardDescription>
                </div>
                <Badge
                  variant="outline"
                  className="bg-blue-50 text-blue-700 border-blue-200"
                >
                  Editable Fields
                </Badge>
              </div>
            </CardHeader>

            <CardContent className="pt-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* a. Total Compensation */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label
                      htmlFor="totalCompensation"
                      className="text-sm font-semibold text-gray-800"
                    >
                      a. Total Compensation (Annual CTC){" "}
                      <span className="text-red-500">*</span>
                    </Label>
                    <span className="text-xs text-blue-600 font-medium">
                      {formatCurrency(parsedTotalComp)} / year
                    </span>
                  </div>
                  <div className="relative rounded-md shadow-sm">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                      <span className="text-gray-500 sm:text-sm font-semibold">
                        ₹
                      </span>
                    </div>
                    <Input
                      id="totalCompensation"
                      type="text"
                      placeholder="e.g. 1200000"
                      value={totalCompensation}
                      onChange={(e) =>
                        setTotalCompensation(
                          e.target.value.replace(/[^0-9.]/g, ""),
                        )
                      }
                      className="pl-8 font-mono text-base font-semibold text-gray-900 border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                  <p className="text-xs text-gray-500">
                    Gross annual cost to company (CTC) including all benefits
                    and variable pay.
                  </p>
                </div>

                {/* b. Variable */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label
                      htmlFor="variablePay"
                      className="text-sm font-semibold text-gray-800"
                    >
                      b. Variable Pay (Annual Bonus / Incentive)
                    </Label>
                    <span className="text-xs text-amber-600 font-medium">
                      {formatCurrency(parsedVariable)}
                      {parsedTotalComp > 0
                        ? ` (${((parsedVariable / parsedTotalComp) * 100).toFixed(1)}% of CTC)`
                        : ""}
                    </span>
                  </div>
                  <div className="relative rounded-md shadow-sm">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                      <span className="text-gray-500 sm:text-sm font-semibold">
                        ₹
                      </span>
                    </div>
                    <Input
                      id="variablePay"
                      type="text"
                      placeholder="e.g. 150000"
                      value={variablePay}
                      onChange={(e) =>
                        setVariablePay(e.target.value.replace(/[^0-9.]/g, ""))
                      }
                      className="pl-8 font-mono text-base font-semibold text-gray-900 border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                  <p className="text-xs text-gray-500">
                    Annual performance bonus or sales incentive paid
                    periodically.
                  </p>
                </div>
              </div>

              {/* High-Level Overview Cards */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 pt-2">
                <div className="p-4 rounded-xl bg-blue-50/70 border border-blue-100">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-blue-700 uppercase tracking-wider">
                      Total CTC
                    </span>
                    <TrendingUp className="h-4 w-4 text-blue-600" />
                  </div>
                  <p className="text-xl font-bold text-blue-900 mt-2">
                    {formatCurrency(parsedTotalComp)}
                  </p>
                  <p className="text-[11px] text-blue-600 mt-0.5">
                    Annual Total
                  </p>
                </div>

                <div className="p-4 rounded-xl bg-emerald-50/70 border border-emerald-100">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-emerald-700 uppercase tracking-wider">
                      Fixed Salary
                    </span>
                    <Wallet className="h-4 w-4 text-emerald-600" />
                  </div>
                  <p className="text-xl font-bold text-emerald-900 mt-2">
                    {formatCurrency(parsedFixedPay)}
                  </p>
                  <p className="text-[11px] text-emerald-600 mt-0.5">
                    Annual Base Pay
                  </p>
                </div>

                <div className="p-4 rounded-xl bg-amber-50/70 border border-amber-100">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-amber-700 uppercase tracking-wider">
                      Variable
                    </span>
                    <Sparkles className="h-4 w-4 text-amber-600" />
                  </div>
                  <p className="text-xl font-bold text-amber-900 mt-2">
                    {formatCurrency(parsedVariable)}
                  </p>
                  <p className="text-[11px] text-amber-600 mt-0.5">
                    Incentives / Bonus
                  </p>
                </div>

                <div className="p-4 rounded-xl bg-purple-50/70 border border-purple-100">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-purple-700 uppercase tracking-wider">
                      Monthly Base
                    </span>
                    <Calendar className="h-4 w-4 text-purple-600" />
                  </div>
                  <p className="text-xl font-bold text-purple-900 mt-2">
                    {formatCurrency(monthlyGrossFixed)}
                  </p>
                  <p className="text-[11px] text-purple-600 mt-0.5">
                    Est. Gross / Month
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Section 2 & 3: Company Salary Components & Editable Grid */}
          <Card className="shadow-sm border-gray-200">
            <CardHeader className="pb-4 border-b">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <DollarSign className="h-5 w-5 text-blue-600" />
                    <span>2. Company Salary Components & Fields</span>
                  </CardTitle>
                  {/* <CardDescription>
                    Components assigned to this company via API &bull; Feed monthly values for this employee
                  </CardDescription> */}
                </div>
                <div className="flex items-center gap-2">
                  <Badge
                    variant="outline"
                    className="bg-gray-50 text-gray-600 text-xs"
                  >
                    {companyComponents.length} Active Components
                  </Badge>
                  {componentsSource === "api" ? (
                    <Badge className="bg-green-100 text-green-800 hover:bg-green-100 border-green-200 text-xs">
                      Live API
                    </Badge>
                  ) : (
                    <Badge variant="secondary" className="text-xs">
                      {componentsSource === "local"
                        ? "Cached Config"
                        : "Standard Super List"}
                    </Badge>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={loadSalaryData}
                    disabled={loadingComponents}
                    className="h-8 px-2 text-gray-500 hover:text-gray-900"
                    title="Reload components from API"
                  >
                    <RefreshCw
                      className={`h-3.5 w-3.5 ${loadingComponents ? "animate-spin" : ""}`}
                    />
                  </Button>
                </div>
              </div>
            </CardHeader>

            <CardContent className="pt-6 space-y-6">
              {loadingComponents ? (
                <div className="py-12 text-center text-gray-500 space-y-3">
                  <RefreshCw className="h-8 w-8 mx-auto animate-spin text-blue-600" />
                  <p className="text-sm font-medium">
                    Fetching company salary components from API...
                  </p>
                </div>
              ) : companyComponents.length === 0 ? (
                <div className="py-12 text-center text-gray-500 space-y-3">
                  <AlertCircle className="h-8 w-8 mx-auto text-amber-500" />
                  <p className="text-sm font-medium">
                    No salary components assigned for this company yet.
                  </p>
                  <p className="text-xs text-gray-400">
                    Configure company payroll components in Settings or use
                    standard components.
                  </p>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleAutoDistribute}
                  >
                    Load Default Components
                  </Button>
                </div>
              ) : (
                <>
                  {/* Grid Table for each component */}
                  <div className="border rounded-xl overflow-hidden shadow-xs">
                    <Table>
                      <TableHeader className="bg-gray-50/80">
                        <TableRow>
                          <TableHead className="w-[300px] font-semibold text-gray-900">
                            Salary Component
                          </TableHead>
                          <TableHead className="w-[140px] font-semibold text-gray-900">
                            Type
                          </TableHead>
                          <TableHead className="w-[220px] font-semibold text-gray-900">
                            Monthly Value (₹){" "}
                            <span className="text-xs text-blue-600 font-normal">
                              Editable
                            </span>
                          </TableHead>
                          <TableHead className="w-[180px] font-semibold text-gray-900">
                            Annualized (₹)
                          </TableHead>
                          <TableHead className="text-right font-semibold text-gray-900">
                            % of Fixed CTC
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {componentsBreakdown.map((row) => {
                          const isEarning = row.isEarning;
                          return (
                            <TableRow
                              key={row.fieldId}
                              className="hover:bg-slate-50/80 transition-colors"
                            >
                              <TableCell>
                                <div className="space-y-0.5">
                                  <p className="font-semibold text-gray-900 text-sm">
                                    {row.name}
                                  </p>
                                  <p className="text-xs text-gray-400 font-mono">
                                    ID: {row.fieldId}{" "}
                                    {row.code ? `• ${row.code}` : ""}
                                  </p>
                                </div>
                              </TableCell>

                              <TableCell>
                                <Badge
                                  variant="outline"
                                  className={
                                    isEarning
                                      ? "bg-emerald-50 text-emerald-700 border-emerald-200 font-medium"
                                      : "bg-rose-50 text-rose-700 border-rose-200 font-medium"
                                  }
                                >
                                  {isEarning ? "+ EARNING" : "- DEDUCTION"}
                                </Badge>
                              </TableCell>

                              <TableCell>
                                <div className="relative rounded-md max-w-[200px]">
                                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-2.5">
                                    <span className="text-gray-400 text-xs font-semibold">
                                      ₹
                                    </span>
                                  </div>
                                  <Input
                                    type="text"
                                    placeholder="0"
                                    value={componentValues[row.fieldId] || ""}
                                    onChange={(e) =>
                                      handleComponentChange(
                                        row.fieldId,
                                        e.target.value,
                                      )
                                    }
                                    className="pl-7 py-1.5 h-9 font-mono text-sm font-semibold border-gray-300 focus:border-blue-500 focus:ring-blue-500 text-gray-900 bg-white"
                                  />
                                </div>
                              </TableCell>

                              <TableCell className="font-mono text-sm font-medium text-gray-700">
                                {formatCurrency(row.annualVal)}
                              </TableCell>

                              <TableCell className="text-right font-mono text-xs text-gray-600">
                                {row.percentOfFixed > 0
                                  ? `${row.percentOfFixed}%`
                                  : "-"}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>

                  {/* Summary Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 rounded-xl bg-slate-50 border border-slate-200">
                    <div className="space-y-1">
                      <span className="text-xs font-semibold text-emerald-700 flex items-center gap-1.5">
                        <TrendingUp className="h-4 w-4" /> Gross Earnings
                        (Monthly)
                      </span>
                      <p className="text-xl font-bold text-emerald-900">
                        {formatCurrency(totalEarningsMonthly)}
                      </p>
                      <p className="text-[11px] text-gray-500">
                        Annual: {formatCurrency(totalEarningsMonthly * 12)}
                      </p>
                    </div>

                    <div className="space-y-1">
                      <span className="text-xs font-semibold text-rose-700 flex items-center gap-1.5">
                        <TrendingDown className="h-4 w-4" /> Total Deductions
                        (Monthly)
                      </span>
                      <p className="text-xl font-bold text-rose-900">
                        {formatCurrency(totalDeductionsMonthly)}
                      </p>
                      <p className="text-[11px] text-gray-500">
                        Annual: {formatCurrency(totalDeductionsMonthly * 12)}
                      </p>
                    </div>

                    <div className="space-y-1">
                      <span className="text-xs font-semibold text-blue-700 flex items-center gap-1.5">
                        <Wallet className="h-4 w-4" /> Net In-Hand Salary
                        (Monthly)
                      </span>
                      <p className="text-xl font-bold text-blue-900">
                        {formatCurrency(netTakeHomeMonthly)}
                      </p>
                      <p className="text-[11px] text-gray-500">
                        Annual: {formatCurrency(netTakeHomeMonthly * 12)}
                      </p>
                    </div>
                  </div>

                  {/* Allocation Warning / Info */}
                  {parsedFixedPay > 0 && (
                    <div className="flex items-center justify-between text-xs px-3 py-2 rounded-lg bg-blue-50/70 border border-blue-200 text-blue-800">
                      <span className="flex items-center gap-1.5">
                        <CheckCircle2 className="h-4 w-4 text-blue-600" />
                        Allocated Monthly Earnings:{" "}
                        <strong>
                          {formatCurrency(totalEarningsMonthly)}
                        </strong>{" "}
                        vs Base Fixed Target:{" "}
                        <strong>{formatCurrency(monthlyGrossFixed)}</strong>
                      </span>
                      <span className="font-mono">
                        {monthlyGrossFixed > 0
                          ? `${((totalEarningsMonthly / monthlyGrossFixed) * 100).toFixed(1)}% allocated`
                          : ""}
                      </span>
                    </div>
                  )}
                </>
              )}

              {/* Section 4: Save Actions */}
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 border-t border-gray-100">
                <div className="text-xs text-gray-500">
                  {saveSuccessMessage && (
                    <span className="text-green-600 font-medium flex items-center gap-1">
                      <CheckCircle2 className="h-4 w-4" /> {saveSuccessMessage}
                    </span>
                  )}
                </div>

                <div className="flex items-center space-x-3 w-full sm:w-auto">
                  {onBack && (
                    <Button
                      variant="outline"
                      onClick={onBack}
                      disabled={savingSalary}
                    >
                      Cancel
                    </Button>
                  )}
                  <Button
                    onClick={handleSaveSalary}
                    disabled={savingSalary || loadingComponents}
                    className="bg-blue-600 hover:bg-blue-700 text-white shadow-sm flex-1 sm:flex-initial"
                  >
                    {savingSalary ? (
                      <>
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />{" "}
                        Saving Salary Details...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" /> 4. Save Salary Details
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default EmployeeSalaryManager;
